import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import { useSessionComms } from '../../contexts/SessionCommsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { sessionApi } from '../../services/sessionApi';
import storageManager from '../../utils/StorageManager';
import { buildAutoJoinAlias, generateRandomGuestAlias, isPlayerOneName } from '../../utils/guestAlias';
import { ACTIVE_SESSION_CONTEXT_KEY, ACTIVE_SESSION_CONTEXT_TTL_HOURS, SESSION_CONTEXT_CHANGED_EVENT, SESSION_PRESENCE_TTL_HOURS } from '../../contexts/SessionCommsContext';

function GuestSessionPage() {
    const { code } = useParams();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const { emitRealtimeEvent, activeSessionContext } = useSessionComms();
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const normalizedCode = useMemo(() => String(code || '').trim().toUpperCase(), [code]);
    const storedDefaultAlias = useMemo(() => storageManager.getPlayerName(), []);
    const [alias, setAlias] = useState(() => storedDefaultAlias);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState('');
    const [autoAliasSeed] = useState(() => generateRandomGuestAlias());

    useEffect(() => {
        if (authLoading) return;
        const normalizedAlias = String(alias || '').trim();
        const shouldAssignAuto = !normalizedAlias || isPlayerOneName(normalizedAlias) || (!isAuthenticated && normalizedAlias === String(storedDefaultAlias || '').trim());
        if (!shouldAssignAuto) return;
        setAlias(autoAliasSeed);
    }, [alias, authLoading, autoAliasSeed, isAuthenticated, storedDefaultAlias]);

    const saveActiveContext = (context) => {
        storageManager.saveTemporaryData(
            ACTIVE_SESSION_CONTEXT_KEY,
            context,
            ACTIVE_SESSION_CONTEXT_TTL_HOURS
        );
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(SESSION_CONTEXT_CHANGED_EVENT));
        }
    };

    const handleJoin = async (event) => {
        event.preventDefault();
        setError('');

        if (!normalizedCode) {
            setError('Session code is missing in this link.');
            return;
        }

        const activeCode = String(activeSessionContext?.code || '').trim().toUpperCase();
        if (activeCode && activeCode !== normalizedCode) {
            setError(`You are already in session ${activeCode}. Leave it before joining another.`);
            return;
        }

        try {
            setJoining(true);
            const preJoinSessionData = await sessionApi.getGuestSession(normalizedCode);
            const preJoinSession = preJoinSessionData.session || null;
            const activeAliases = Array.isArray(preJoinSession?.participants)
                ? preJoinSession.participants.map((participant) => participant.alias)
                : [];
            const resolvedAlias = buildAutoJoinAlias({
                typedAlias: alias,
                storedAlias: storedDefaultAlias,
                isAuthenticated,
                activeAliases,
            });
            setAlias(resolvedAlias);

            const joinData = await sessionApi.joinGuestSession(normalizedCode, { alias: resolvedAlias });
            const sessionData = await sessionApi.getGuestSession(normalizedCode);
            const session = sessionData.session || null;
            const finalAlias = joinData.participant?.alias || resolvedAlias;

            storageManager.savePlayerName(finalAlias);
            storageManager.saveTemporaryData(
                `guest_session_presence_${normalizedCode}`,
                { alias: finalAlias },
                SESSION_PRESENCE_TTL_HOURS
            );
            saveActiveContext({
                code: normalizedCode,
                alias: finalAlias,
                role: session?.hostAlias?.toLowerCase() === finalAlias.toLowerCase() ? 'host' : 'participant',
                joinUrl: session?.joinUrl || `${window.location.origin}/session/${normalizedCode}`,
            });
            emitRealtimeEvent('session-updated', {
                code: normalizedCode,
                action: 'joined',
                alias: finalAlias,
            }, normalizedCode).catch(() => null);
            emitRealtimeEvent('messages-updated', {
                code: normalizedCode,
                action: 'joined',
                alias: finalAlias,
            }, normalizedCode).catch(() => null);

            navigate('/');
        } catch (requestError) {
            setError(requestError.message || 'Failed to join session');
        } finally {
            setJoining(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }}>
            <BackButton to="/" />
            <div style={{
                width: '100%',
                maxWidth: '460px',
                backgroundColor: theme.bgDark,
                border: `2px solid ${theme.secondary}`,
                borderRadius: '12px',
                padding: '20px',
                display: 'grid',
                gap: '12px'
            }}>
                <h1 style={{ margin: 0 }}>Join Session</h1>
                <div style={{ color: theme.textSecondary }}>Code: {normalizedCode || 'Unknown'}</div>
                <p style={{ color: theme.textSecondary, margin: 0 }}>
                    Chat and voice run as popups inside the app. Join once, then continue from the home screen.
                </p>
                {activeSessionContext?.code && String(activeSessionContext.code).toUpperCase() !== normalizedCode && (
                    <div style={{
                        color: theme.warning,
                        backgroundColor: theme.bgMedium,
                        border: `1px solid ${theme.secondary}`,
                        borderRadius: '8px',
                        padding: '10px'
                    }}>
                        Active session detected: {String(activeSessionContext.code).toUpperCase()}. Leave the current session first.
                    </div>
                )}

                <form onSubmit={handleJoin} style={{ display: 'grid', gap: '10px' }}>
                    <input
                        value={alias}
                        onChange={(event) => setAlias(event.target.value)}
                        placeholder="Auto-generated username"
                        style={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: `2px solid ${theme.secondary}`,
                            backgroundColor: theme.bgMedium,
                            color: theme.textPrimary,
                            fontFamily: 'inherit',
                        }}
                    />
                    <button
                        type="submit"
                        disabled={joining}
                        style={{
                            padding: '12px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: theme.accent,
                            color: theme.textPrimary,
                            cursor: joining ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                            fontWeight: 'bold',
                            opacity: joining ? 0.7 : 1
                        }}
                    >
                        {joining ? 'Joining...' : 'Join and Continue'}
                    </button>
                </form>

                {error && <div style={{ color: theme.danger }}>{error}</div>}
            </div>
        </div>
    );
}

export default GuestSessionPage;
